import React, {useCallback, useMemo, useState} from "react";
import {DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import PartnerDataGrid from "../../../../../containers/data-grid";
import {AccountingTransactionTypes} from "../../../../../../core/constants/enums";
import IconButton from "../../../../base/button/icon_button";
import {CircularProgress, SvgIcon} from "@mui/material";
import {ReactComponent as RemoveIcon} from "../../../../../../assets/images/remove.svg";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import Regexes from "../../../../../../core/models/regexes";
import {PayMultipleInvoicesDialogSummaryDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import {ReceiptLongRounded} from "@mui/icons-material";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import TransactionReceiptDialog from "../../../transaction-receipt-dialog";


const dataGridSaveConfig = {
    name: 'PayMultipleInvoicesDialogSummarySectionDataGrid',
    version: 1,
}

const initReceiptDialogState = {title: '', open: false, customerId: undefined, receiptId: undefined, close: () => void 0};

const PayMultipleInvoicesDialogSummarySection = ({
                                                     customerId,
                                                     totalAmount,
                                                     selectedInvoices,
                                                     selectedCredits,
                                                     appliedPayments,
                                                     removeCredit: removeCreditProps,
                                                 }) => {
    const [removingCredit, setRemovingCredit] = useState(false);
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);

    const operatorAccessLevels = useAccessLevels();
    const isMounted = useIsMounted();

    const columns = useMemo(() =>
        /**@type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.sales.paymentReceipts ? [
                {
                    name: PayMultipleInvoicesDialogSummaryDataGridColumnNames.remove,
                    alignment: DataGridColumnAlignments.center,
                    type: DataGridColumnTypes.element,
                    pinned: true,
                    sortable: false,
                    pinnedToggleable: false,
                    resizable: false,
                    visibilityToggleable: false,
                    width: 0,
                },
            ] : []),
            {
                name: PayMultipleInvoicesDialogSummaryDataGridColumnNames.receipt,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            },
            {
                title: 'Transaction No',
                name: PayMultipleInvoicesDialogSummaryDataGridColumnNames.transactionNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 120,
            },
            {
                title: 'Date',
                name: PayMultipleInvoicesDialogSummaryDataGridColumnNames.date,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
                sortable: false,
            },
            {
                title: 'Type',
                name: PayMultipleInvoicesDialogSummaryDataGridColumnNames.type,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                format: (val) => {
                    switch (val) {
                        case AccountingTransactionTypes.invoice_ui:
                            return "Invoice"
                        case AccountingTransactionTypes.CreditMemo:
                            return "Payment Memo"
                        case AccountingTransactionTypes.payment:
                            return "Online Payment"
                        case AccountingTransactionTypes.otherPayment:
                            return "Other Payment"
                        default:
                            val = val.replace(/_+/g, '')
                            return val[0] + val.slice(1).replace(Regexes.CamelCaseFinder, (c) => ` ${c}`)
                    }
                }
            },
            {
                title: 'Reference No',
                name: PayMultipleInvoicesDialogSummaryDataGridColumnNames.referenceNo,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: 110,
            },
            {
                title: 'Debit',
                name: PayMultipleInvoicesDialogSummaryDataGridColumnNames.debit,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
            },
            {
                title: 'Credit',
                name: PayMultipleInvoicesDialogSummaryDataGridColumnNames.credit,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.money,
                sortable: false,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
            },
        ], [operatorAccessLevels.packages.sales.paymentReceipts])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Calls the [removeCreditProps] callback to remove the selected credit.
     * @return {Promise<void>}
     */
    const removeCredit = useCallback(async (credit) => {
        setRemovingCredit(true);
        await removeCreditProps(credit)
        if (!isMounted())
            return;
        setRemovingCredit(false)
    }, [removeCreditProps, isMounted])

    const dataGridData = useMemo(() => {
        return [
            ...(selectedInvoices?.map(e => ({
                ...e.transactionRecord,
                transactionTypeName: AccountingTransactionTypes.invoice_ui,
                debit: e.isDebit ? e.balance : null,
                credit: e.isDebit ? null : e.balance,
            })) ?? []),
            ...(selectedCredits?.map(e => ({
                ...e,
                canRemove: true,
                transactionTypeName: e.transactionTypeName ?? AccountingTransactionTypes.CreditMemo,
                debit: e.isDebit ? e.amount : null,
                credit: e.isDebit ? null : e.amount,
            })) ?? []),
            ...(appliedPayments?.map(e => ({
                ...e,
                transactionTypeName: e.transactionTypeName ?? AccountingTransactionTypes.payment,
                receiptId: e.receiptId,
                debit: e.isDebit ? e.amount : null,
                credit: e.isDebit ? null : e.amount,
            })) ?? []),
        ]
    }, [selectedInvoices, selectedCredits, appliedPayments])

    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: customerId,
            // TODO: receipt id for the print receipt
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [customerId])

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return dataGridData.map(transaction => ({
            key: transaction.id,
            data: transaction,
            cells: {
                [PayMultipleInvoicesDialogSummaryDataGridColumnNames.receipt]:
                    transaction.transactionTypeName === AccountingTransactionTypes.payment && transaction.receiptId ?
                        <IconButton
                            color={'paper.primary'}
                            onPressed={() => openReceiptDialog(transaction)}
                        >
                            <ReceiptLongRounded className={'icon normal'}/>
                        </IconButton>
                        : ' ',
                [PayMultipleInvoicesDialogSummaryDataGridColumnNames.remove]:
                    transaction?.canRemove
                        ? <IconButton
                            onPressed={() => removeCredit(transaction)}
                            disabled={removingCredit}
                            color={'paper.error'}
                            className={'remove-icon'}
                        >
                            {
                                removingCredit
                                    ? <CircularProgress size={20} color={'secondary'}/>
                                    : <SvgIcon component={RemoveIcon} className='icon normal'/>
                            }
                        </IconButton>
                        : <></>,
                [PayMultipleInvoicesDialogSummaryDataGridColumnNames.transactionNo]: transaction.transactionNo,
                [PayMultipleInvoicesDialogSummaryDataGridColumnNames.date]: transaction.submittedDate,
                [PayMultipleInvoicesDialogSummaryDataGridColumnNames.type]: transaction.transactionTypeName,
                [PayMultipleInvoicesDialogSummaryDataGridColumnNames.referenceNo]: transaction.referenceDisplay,
                [PayMultipleInvoicesDialogSummaryDataGridColumnNames.debit]: transaction.debit,
                [PayMultipleInvoicesDialogSummaryDataGridColumnNames.credit]: transaction.credit,
            },
        }))
    }, [dataGridData, openReceiptDialog, removeCredit, removingCredit])

    return (
        <>
            <div className='w-100'>
                <div className={'w-100 d-flex px-4'}>
                    {
                        totalAmount > 0 &&
                        <p className={'text-sm text-color-2'}>
                            Here is a preview of your selected invoices and applied credits so far. You can still add payments of your
                            choice to complete these invoices
                        </p>
                    }
                    {
                        totalAmount <= 0 &&
                        <p className={'text-sm text-color-2'}>
                            Your selected Invoices are fully paid with the selected credits and payments. Thanks for your purchases.
                        </p>
                    }
                </div>
                <PartnerDataGrid
                    {...dataGridSaveConfig}
                    initialLayoutState={{
                        columns: columns,
                    }}
                    columns={dataGridColumns}
                    rows={dataGridRows}
                    hideExporting
                    hideFooter
                    visibleRows={5}
                    state={{
                        density: dataGridDensity,
                    }}
                />
            </div>
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
        </>
    )
}

export default PayMultipleInvoicesDialogSummarySection;
